import dayjs from '~/timeInit';

import { SYDNEY_TIMEZONE } from '~/services/TimeService';

import type {
  ScoredTicketRisk,
  TicketRiskBand,
  TicketRiskHardBreakdown,
  TicketRiskReport,
  TicketRiskTierChange,
  TicketRiskTrajectory,
  TicketRiskWatch,
} from '~/types/ticketRisk';

export const EMPTY_VALUE = '-';

const ZENDESK_TICKET_URL = 'https://luxuryescapeshelp.zendesk.com/agent/tickets';
const ZENDESK_BRAND_ID = '11031997673999';

export type RiskTier = 'Critical' | 'High' | 'Medium' | 'Low';

export type JourneyPhase = 'In-stay' | 'Pre-stay' | 'Post-stay' | 'Unknown';

/** A stay that ended more than this many days ago no longer frames the ticket. */
export const POST_STAY_WINDOW_DAYS = 90;

/** Display-only cap — day counts collapse to "+21 days" past it; never moves a ticket between phases. */
export const JOURNEY_DAYS_DISPLAY_CAP = 21;

/**
 * Thresholds that turn a raw count into a signal worth flagging. Shared by the dashboard's
 * signal filters and the grid's per-row signal strip so the two can never disagree.
 */
export const SEVEN_DAYS_IN_MINUTES = 7 * 24 * 60;
export const REASSIGNMENT_SIGNAL_THRESHOLD = 3;
export const INTERNAL_COMMENT_SIGNAL_THRESHOLD = 10;
export const REOPEN_SIGNAL_THRESHOLD = 2;

export const RISK_TIERS: Array<RiskTier> = ['Critical', 'High', 'Medium', 'Low'];

export const TIME_OPEN_FILTER_DAYS = [7, 14, 21, 30] as const;
export type TimeOpenFilter = (typeof TIME_OPEN_FILTER_DAYS)[number];

/** `?timeOpen=7` from a deep link (the daily aging email); anything else means no filter. */
export const parseTimeOpenParam = (value: string | null): TimeOpenFilter | null =>
  TIME_OPEN_FILTER_DAYS.find((days) => String(days) === value) ?? null;

/** `?riskTier=Critical,High` from a deep link; unknown tiers are dropped. */
export const parseRiskTierParam = (value: string | null): Array<RiskTier> =>
  value ? RISK_TIERS.filter((tier) => value.split(',').includes(tier)) : [];
export const JOURNEY_PHASES: Array<JourneyPhase> = ['In-stay', 'Pre-stay', 'Post-stay', 'Unknown'];

const BAND_TO_TIER: Record<TicketRiskBand, RiskTier> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * LTV bands as svc-support assigns them, most to least valuable — the order is its own
 * `ltvRank` in `src/features/ticketRiskScoring/services/scoring.service.ts`. The thresholds
 * live server-side; these are the labels for them.
 */
export const LTV_BANDS = ['vip', 'top_tier', 'valuable', 'standard'] as const;

export type LtvBand = (typeof LTV_BANDS)[number];

export const LTV_BAND_LABELS: Record<LtvBand, string> = {
  vip: 'VIP (≥ $50k)',
  top_tier: 'Top tier (≥ $20k)',
  valuable: 'Valuable (≥ $5k)',
  standard: 'Standard',
};

/** Null for an absent band and for one svc-support adds that this build has no label for. */
export function toLtvBand(value: string | null | undefined): LtvBand | null {
  return value != null && (LTV_BANDS as ReadonlyArray<string>).includes(value) ? (value as LtvBand) : null;
}

/** Société tiers the MUI theme has `palette.loyaltyTiers` entries for. */
export const SOCIETE_TIER_THEME_KEYS = {
  Bronze: 'bronze',
  Silver: 'silver',
  Gold: 'gold',
  Platinum: 'platinum',
} as const;

export type SocieteTier = keyof typeof SOCIETE_TIER_THEME_KEYS;

const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';
const SHORT_DATE_FORMAT = 'DD MMM';
const SHORT_DATE_TIME_FORMAT = 'DD MMM HH:mm';

/** Tier → MUI palette key, so the tier reads the same on every chip, dot and bar. */
export const RISK_TIER_COLORS: Record<RiskTier, 'error' | 'warning' | 'info' | 'success'> = {
  Critical: 'error',
  High: 'warning',
  Medium: 'info',
  Low: 'success',
};

export interface CxTicketRow {
  id: string;
  subject: string | null;

  // Scores
  riskScore: number;
  riskTier: RiskTier;
  /**
   * Entered High/Critical since yesterday's run — was Low, Medium or unseen (every High/Critical
   * ticket, on svc-support's first day). High → Critical is an up-move, not New.
   */
  isNew: boolean;
  /** Null only when svc-support predates the tier-change fields. */
  tierChange: TicketRiskTierChange | null;
  /** The tier in yesterday's run; null when the ticket was not scored then. */
  previousTier: RiskTier | null;
  hardScore: number;
  llmScore: number | null;
  divergent: boolean;
  percentile: number | null;
  criticalRank: number | null;
  /** False when the LLM call failed for this ticket and the hard score carried the blend. */
  hasLlmAssessment: boolean;
  llmTrajectory: TicketRiskTrajectory | null;
  llmConfidence: number | null;
  llmReasoning: string | null;
  llmRiskFactors: Array<string>;
  llmEarlyWarnings: Array<string>;
  /** The LLM found a grounded acceptance of the resolution — excluded from critical. */
  resolutionAccepted: boolean;

  // Watching and comments
  /** The one leader handling the ticket; null when nobody is. */
  watcher: TicketRiskWatch | null;
  /** The watcher's name or '' — what the grid sorts/filters on and the CSV exports. */
  watcherName: string;
  /** True when the signed-in leader is the watcher — drives Watch / Take over / Stop watching. */
  isWatchedByMe: boolean;
  /** Live comments on the ticket's thread; 0 when svc-support predates comments. */
  commentCount: number;
  /** Of those, posted in the last hour — what the grid badges. */
  recentCommentCount: number;
  /** Who commented last and when; null when nobody has. */
  lastComment: { authorName: string; createdAt: string } | null;

  // Hard score components
  classificationScore: number;
  /** The classification that scored, e.g. `reason:Complaint`. */
  classificationSource: string | null;
  classificationLift: number | null;
  riskFlagScore: number;
  matchedRiskFlags: Array<string>;
  keywordSignalScore: number;
  matchedKeywordSignals: Array<string>;
  /** A legal / fair-trading ownership marker was found — the ticket is forced to critical. */
  legalHold: boolean;
  reassignPoints: number;
  agingPoints: number;
  awaitingReviewPoints: number;
  requesterWaitPoints: number;
  staleContactPoints: number;
  internalCommentPoints: number;
  reopenPoints: number;
  operationalRiskScore: number;
  inStayPoints: number;
  upcomingCheckInPoints: number;
  recentStayPoints: number;
  ltvPoints: number;
  societePoints: number;
  luxPlusPoints: number;
  customerRiskScore: number;

  // Ticket
  created: string;
  lastUpdated: string | null;
  statusUpdated: string | null;
  ticketStatus: string;
  team: string | null;
  assignee: string | null;
  teamLeader: string | null;
  ticketReason: string | null;
  subcategory: string | null;
  triageCategory: string | null;
  priority: string | null;
  tags: Array<string>;
  replies: number;
  internalComments: number;
  agentsTouched: number;
  reassignCount: number;
  reopenCount: number;
  requesterWaitMinutes: number;
  caseAgeDays: number;
  hoursSinceOpen: number;
  hoursAwaitingReview: number;
  hoursSinceLastContact: number;
  lastAgentReplyAt: string | null;
  /** Display label, e.g. "Very negative". */
  sentiment: string | null;
  /** Collapsed to three buckets so the sentiment filters and counters can compare exactly. */
  sentimentBucket: SentimentBucket | null;

  // Customer
  journeyPhase: JourneyPhase;
  checkInDays: number | null;
  daysSinceCheckIn: number | null;
  currentStayCheckInDate: string | null;
  currentStayCheckOutDate: string | null;
  nextCheckInDate: string | null;
  nextCheckOutDate: string | null;
  lastCheckOutDate: string | null;
  societeTier: string | null;
  luxPlusMember: boolean | null;
  customerLifetimeValue: number | null;
  /** Display label, e.g. "Top tier". */
  ltvBand: string | null;
  /** svc-support's own band token, which ranks the value and keys its label and chip colour. */
  ltvBandKey: LtvBand | null;
  /** Days since the customer's last checkout; null when they have never stayed. */
  daysSinceLastCheckOut: number | null;
  authorityKeywords: Array<string>;
  authorityConfirmed: boolean;
}

export function bandToTier(band: TicketRiskBand): RiskTier {
  return BAND_TO_TIER[band];
}

/**
 * One line on how the tier moved since yesterday's run, for the New chip's tooltip and the
 * detail dialog. Null when svc-support served no tier change (it predates the fields).
 */
export function describeTierChange(row: Pick<CxTicketRow, 'riskTier' | 'tierChange' | 'previousTier'>): string | null {
  switch (row.tierChange) {
    case 'new':
      return 'Not scored yesterday';
    case 'up':
      return `Up from ${row.previousTier} yesterday`;
    case 'down':
      return `Down from ${row.previousTier} yesterday`;
    case 'same':
      return `${row.riskTier} yesterday too`;
    default:
      return null;
  }
}

/** `very_negative` → `Very negative`; used for sentiment, LTV bands and authority keywords. */
export function humanize(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const spaced = value.replace(/[_-]+/g, ' ').trim();
  if (!spaced) {
    return null;
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Display label for the ticket's state. Prefers the Zendesk custom status ("Awaiting Review")
 * because that is what CX works from, falling back to the native status when there is none.
 */
function toTicketStatusLabel(status: string, customStatusName: string | null): string {
  return customStatusName ?? humanize(status) ?? status;
}

export function toJourneyPhase(
  inStayNow: boolean,
  upcomingCheckInDays: number | null,
  daysSinceLastCheckOut: number | null,
): JourneyPhase {
  if (inStayNow) {
    return 'In-stay';
  }
  if (upcomingCheckInDays !== null) {
    return 'Pre-stay';
  }
  if (daysSinceLastCheckOut !== null && daysSinceLastCheckOut <= POST_STAY_WINDOW_DAYS) {
    return 'Post-stay';
  }
  return 'Unknown';
}

const OVER_CAP_LABEL = `+${JOURNEY_DAYS_DISPLAY_CAP} days`;

export type JourneyDayFacts = Pick<
  CxTicketRow,
  'journeyPhase' | 'checkInDays' | 'daysSinceCheckIn' | 'daysSinceLastCheckOut'
>;

export function toJourneyPhaseLabel(row: JourneyDayFacts): string {
  const detail = toJourneyDayDetail(row);
  return detail ? `${row.journeyPhase} · ${detail}` : row.journeyPhase;
}

function toJourneyDayDetail(row: JourneyDayFacts): string | null {
  switch (row.journeyPhase) {
    case 'In-stay': {
      if (row.daysSinceCheckIn == null) {
        return null;
      }
      // Check-in day is day 1, so the count matches how a guest would say it.
      const dayOfStay = row.daysSinceCheckIn + 1;
      return dayOfStay > JOURNEY_DAYS_DISPLAY_CAP ? OVER_CAP_LABEL : `day ${dayOfStay}`;
    }
    case 'Pre-stay': {
      if (row.checkInDays == null) {
        return null;
      }
      if (row.checkInDays > JOURNEY_DAYS_DISPLAY_CAP) {
        return OVER_CAP_LABEL;
      }
      return row.checkInDays <= 0 ? 'today' : `in ${formatCount(row.checkInDays, 'day')}`;
    }
    case 'Post-stay': {
      if (row.daysSinceLastCheckOut == null) {
        return null;
      }
      if (row.daysSinceLastCheckOut > JOURNEY_DAYS_DISPLAY_CAP) {
        return OVER_CAP_LABEL;
      }
      return row.daysSinceLastCheckOut <= 0 ? 'today' : `${formatCount(row.daysSinceLastCheckOut, 'day')} ago`;
    }
    default:
      return null;
  }
}

const JOURNEY_SORT_PHASE_BAND = 10_000;

/**
 * Journey order first (In-stay, Pre-stay, Post-stay, Unknown), most imminent first within
 * each phase — ascending reads "closest to being in-house right now".
 */
export function toJourneySortKey(row: JourneyDayFacts): number {
  const days =
    row.journeyPhase === 'In-stay'
      ? row.daysSinceCheckIn
      : row.journeyPhase === 'Pre-stay'
        ? row.checkInDays
        : row.journeyPhase === 'Post-stay'
          ? row.daysSinceLastCheckOut
          : null;
  const withinPhase =
    days == null ? JOURNEY_SORT_PHASE_BAND - 1 : Math.min(Math.max(days, 0), JOURNEY_SORT_PHASE_BAND - 2);

  return JOURNEY_PHASES.indexOf(row.journeyPhase) * JOURNEY_SORT_PHASE_BAND + withinPhase;
}

export type JourneyTooltipFacts = JourneyDayFacts &
  Pick<
    CxTicketRow,
    'currentStayCheckInDate' | 'currentStayCheckOutDate' | 'nextCheckInDate' | 'nextCheckOutDate' | 'lastCheckOutDate'
  >;

function formatCalendarDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.format(parsed.year() === dayjs().year() ? SHORT_DATE_FORMAT : `${SHORT_DATE_FORMAT} YYYY`);
}

function formatStayRange(checkIn: string | null | undefined, checkOut: string | null | undefined): string | null {
  const from = formatCalendarDate(checkIn);
  const to = formatCalendarDate(checkOut);
  if (from && to) {
    return `${from} – ${to}`;
  }
  return from ?? to;
}

/**
 * The dates behind the Journey chip's label; rows scored before svc-support sent dates fall
 * back to the exact day counts the capped label leaves out. Empty renders as no tooltip.
 */
export function toJourneyTooltip(row: JourneyTooltipFacts): string {
  const parts: Array<string> = [];

  if (row.journeyPhase === 'In-stay') {
    const stay = formatStayRange(row.currentStayCheckInDate, row.currentStayCheckOutDate);
    if (stay) {
      parts.push(`Stay ${stay}`);
    } else if (row.daysSinceCheckIn != null && row.daysSinceCheckIn + 1 > JOURNEY_DAYS_DISPLAY_CAP) {
      parts.push(`Day ${row.daysSinceCheckIn + 1} of stay`);
    }
    const nextStay = formatStayRange(row.nextCheckInDate, row.nextCheckOutDate);
    if (nextStay) {
      parts.push(`Next stay ${nextStay}`);
    } else if (row.checkInDays != null) {
      parts.push(`Next check-in ${row.checkInDays <= 0 ? 'today' : `in ${formatCount(row.checkInDays, 'day')}`}`);
    }
  }
  if (row.journeyPhase === 'Pre-stay') {
    const nextStay = formatStayRange(row.nextCheckInDate, row.nextCheckOutDate);
    if (nextStay) {
      parts.push(`Next stay ${nextStay}`);
    } else if (row.checkInDays != null && row.checkInDays > JOURNEY_DAYS_DISPLAY_CAP) {
      parts.push(`Checks in ${formatCount(row.checkInDays, 'day')}`);
    }
  }
  if (row.journeyPhase === 'Post-stay' || row.journeyPhase === 'Unknown') {
    const lastCheckOut = formatCalendarDate(row.lastCheckOutDate);
    if (lastCheckOut) {
      parts.push(`Checked out ${lastCheckOut}`);
    } else if (row.daysSinceLastCheckOut != null && row.daysSinceLastCheckOut > JOURNEY_DAYS_DISPLAY_CAP) {
      parts.push(`Checked out ${formatCount(row.daysSinceLastCheckOut, 'day')} ago`);
    }
  }

  return parts.join(' · ');
}

export type SentimentBucket = 'Positive' | 'Neutral' | 'Negative';

/**
 * Zendesk emits several negative grades (`negative`, `very_negative`); the dashboard only
 * ever asks "is this negative", so they collapse into one bucket.
 */
export function toSentimentBucket(zendeskSentiment: string | null): SentimentBucket | null {
  if (!zendeskSentiment) {
    return null;
  }
  const normalized = zendeskSentiment.toLowerCase();
  if (normalized.includes('negative')) {
    return 'Negative';
  }
  if (normalized.includes('positive')) {
    return 'Positive';
  }
  return 'Neutral';
}

export function isSocieteTier(value: string | null): value is SocieteTier {
  return value !== null && value in SOCIETE_TIER_THEME_KEYS;
}

/** svc-support sends Société tiers lower-cased; the theme keys and labels are title-cased. */
function toSocieteTierLabel(societeTier: string | null): string | null {
  return humanize(societeTier);
}

/** ISO instant → epoch ms, or null when absent/unparseable. Used for date bucketing. */
export function toTimestamp(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : null;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return EMPTY_VALUE;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(SYDNEY_TIMEZONE).format(DATE_TIME_FORMAT) : EMPTY_VALUE;
}

/** Axis-friendly day and month, e.g. `19 Aug`. Accepts an ISO instant or epoch milliseconds. */
export function formatShortDate(value: string | number | null | undefined): string {
  if (value == null || value === '') {
    return EMPTY_VALUE;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(SYDNEY_TIMEZONE).format(SHORT_DATE_FORMAT) : EMPTY_VALUE;
}

/** As `formatShortDate`, with the time — the scoring job runs several times a day. */
export function formatShortDateTime(value: string | number | null | undefined): string {
  if (value == null || value === '') {
    return EMPTY_VALUE;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(SYDNEY_TIMEZONE).format(SHORT_DATE_TIME_FORMAT) : EMPTY_VALUE;
}

/** Durations render as hours below a day and as days above it, so long-running tickets stay readable. */
export function formatHours(hours: number | null | undefined): string {
  if (hours == null || !Number.isFinite(hours) || hours <= 0) {
    return EMPTY_VALUE;
  }
  if (hours < HOURS_PER_DAY) {
    const rounded = Math.round(hours * 10) / 10;
    return `${rounded} ${rounded === 1 ? 'hour' : 'hours'}`;
  }
  const days = Math.round((hours / HOURS_PER_DAY) * 10) / 10;
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

export function formatMinutesAsDuration(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes <= 0) {
    return EMPTY_VALUE;
  }
  if (minutes < MINUTES_PER_HOUR) {
    const rounded = Math.round(minutes);
    return `${rounded} ${rounded === 1 ? 'minute' : 'minutes'}`;
  }
  return formatHours(minutes / MINUTES_PER_HOUR);
}

export function formatCount(count: number | null | undefined, singular: string, plural = `${singular}s`): string {
  if (!count) {
    return EMPTY_VALUE;
  }
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Renders anything the UI has no value for as a dash rather than `null` or `NaN`. */
export function formatEmptyValue(value: unknown): string {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return EMPTY_VALUE;
}

/**
 * Joins the parts of a compound field, dropping the ones with nothing in them, so a field
 * reads "Complaint" rather than "Complaint · -".
 */
export function joinDetails(parts: Array<string | null | undefined>, separator = ' · '): string {
  const present = parts.filter((part): part is string => typeof part === 'string' && part.trim().length > 0);

  return present.length > 0 ? present.join(separator) : EMPTY_VALUE;
}

export function formatAud(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) {
    return EMPTY_VALUE;
  }
  return `$${Math.round(amount).toLocaleString('en-AU')}`;
}

/**
 * Lifetime value at a glance — `$42.5k` rather than `$42,500`. Roughly half the width, and the
 * exact dollar is not what a triage decision turns on; the full figure stays in the tooltip and
 * the Customer LTV column.
 */
export function formatCompactAud(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) {
    return EMPTY_VALUE;
  }

  const magnitude = Math.abs(amount);

  if (magnitude >= 1_000_000) {
    return `$${Number((amount / 1_000_000).toFixed(1))}M`;
  }

  if (magnitude >= 1_000) {
    return `$${Number((amount / 1_000).toFixed(1))}k`;
  }

  return `$${Math.round(amount)}`;
}

/** `percentile` is 0 at the riskiest ticket, so it reads better as "top N%". */
export function formatPercentile(percentile: number | null): string {
  if (percentile == null || !Number.isFinite(percentile)) {
    return EMPTY_VALUE;
  }
  return `Top ${Math.max(1, Math.round(percentile))}%`;
}

export function formatConfidence(confidence: number | null): string {
  if (confidence == null || !Number.isFinite(confidence)) {
    return EMPTY_VALUE;
  }
  return `${Math.round(confidence * 100)}%`;
}

/**
 * Display-only casing for svc-support's authority keywords, which arrive lower-cased.
 * Tribunals and regulators read badly title-cased ("Accc"), so the acronyms are spelled out.
 */
const AUTHORITY_ACRONYMS = new Set(['accc', 'vcat', 'ncat', 'qcat', 'afca']);

function formatKeyword(keyword: string): string {
  return keyword
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      AUTHORITY_ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');
}

export function formatKeywordList(keywords: Array<string>): string {
  if (keywords.length === 0) {
    return EMPTY_VALUE;
  }
  return keywords.map(formatKeyword).join(', ');
}

/**
 * Machine prefixes on Zendesk risk-flag and classification tags that say nothing on screen —
 * `zd_rt_insights_churn_risk` reads as "Churn risk" without losing anything.
 */
const TAG_MACHINE_PREFIXES = ['zd_rt_insights_', 'ai_categorised_ticket_', 'ticket_reason_'];

/** `zd_rt_insights_churn_risk` → `Churn risk`; `cs::enquiry::customer::promo_code_offered` → `Promo code offered`. */
export function formatRiskFlagLabel(tag: string): string {
  // cs:: macro tags nest the meaningful part in the last segment.
  const lastSegment = tag.split('::').pop() ?? tag;
  const stripped = TAG_MACHINE_PREFIXES.reduce(
    (value, prefix) => (value.startsWith(prefix) ? value.slice(prefix.length) : value),
    lastSegment,
  );

  return (humanize(stripped) ?? tag).replace(/\bSla\b/g, 'SLA');
}

/** `authority_legal` → `Authority legal`; `reason:Complaint` → `Reason: Complaint`. */
export function formatBreakdownLabel(value: string | null | undefined): string {
  if (!value) {
    return EMPTY_VALUE;
  }
  const [head, ...rest] = value.split(':');
  const label = humanize(head) ?? head;

  if (rest.length === 0) {
    return label;
  }
  const detail = rest.join(':').trim();

  // Tag sources are raw Zendesk machine tags; reasons and subcategories are already human.
  return `${label}: ${head === 'tag' ? formatRiskFlagLabel(detail) : detail}`;
}

/** svc-support names AI risk factors from a fixed vocabulary; `other:<name>` is its escape hatch. */
const OTHER_SIGNAL_PREFIX = 'other:';

/** True when the scorer reached for that escape hatch rather than a name it already knows. */
export function isEmergingSignal(token: string): boolean {
  return token.startsWith(OTHER_SIGNAL_PREFIX);
}

/** `refund_delay` → `Refund Delay`; `other:visa_refusal` → `Visa Refusal`. */
export function formatAiSignalLabel(token: string): string {
  return (isEmergingSignal(token) ? token.slice(OTHER_SIGNAL_PREFIX.length) : token)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/Ltv/g, 'LTV');
}

/** The LLM's risk factors and early warnings, deduplicated into filterable tokens. */
export function getAiSignalTokens(row: Pick<CxTicketRow, 'llmRiskFactors' | 'llmEarlyWarnings'>): Array<string> {
  return Array.from(
    new Set([...row.llmRiskFactors, ...row.llmEarlyWarnings].map((value) => value.trim()).filter(Boolean)),
  );
}

/**
 * `breakdown` is a JSON blob persisted per scoring run and svc-support reshapes it as the
 * scoring model evolves, so a row can legitimately arrive without a component the UI knows
 * about. Reading through these defaults renders zeros and empty lists instead of throwing —
 * a stale contract used to take the whole dashboard down with it.
 */
const HARD_BREAKDOWN_DEFAULTS: TicketRiskHardBreakdown = {
  classificationScore: 0,
  classificationSource: null,
  classificationLift: null,
  riskFlagScore: 0,
  matchedRiskFlags: [],
  keywordSignalScore: 0,
  matchedKeywordSignals: [],
  legalHold: false,
  reassignPoints: 0,
  agingPoints: 0,
  awaitingReviewPoints: 0,
  requesterWaitPoints: 0,
  staleContactPoints: 0,
  internalCommentPoints: 0,
  reopenPoints: 0,
  operationalScore: 0,
  inStayPoints: 0,
  upcomingCheckInPoints: 0,
  recentStayPoints: 0,
  ltvPoints: 0,
  societePoints: 0,
  luxPlusPoints: 0,
  customerScore: 0,
  hardScore: 0,
};

/**
 * Zendesk auto-titles some tickets after their own id ("Luxury Escapes - Ticket #3248509"),
 * which just repeats whatever heading the id is already in — those are dropped rather than
 * shown twice.
 */
export function toDisplaySubject(subject: string | null, ticketId: string): string | null {
  return subject && !subject.includes(ticketId) ? subject : null;
}

export function zendeskTicketUrl(ticketId: string): string {
  return `${ZENDESK_TICKET_URL}/${ticketId}?brand_id=${ZENDESK_BRAND_ID}`;
}

/**
 * @param currentUserId the signed-in leader's `id_member`, so their own watch can be picked out;
 *   null when unknown (nobody reads as "me").
 */
export function toCxTicketRow(scored: ScoredTicketRisk, currentUserId: string | null = null): CxTicketRow {
  const { ticket, customer } = scored.breakdown.factBlock;
  const { llm } = scored.breakdown;
  const hard = { ...HARD_BREAKDOWN_DEFAULTS, ...scored.breakdown.hard };
  const watcher = scored.watch ?? null;

  return {
    id: scored.ticketId,
    subject: ticket.subject,

    riskScore: scored.score,
    riskTier: bandToTier(scored.band),
    isNew: scored.isNew ?? false,
    tierChange: scored.tierChange ?? null,
    previousTier: scored.previousBand ? bandToTier(scored.previousBand) : null,
    watcher,
    watcherName: watcher?.watcherName ?? '',
    isWatchedByMe: watcher !== null && currentUserId !== null && watcher.watcherId === currentUserId,
    commentCount: scored.commentCount ?? 0,
    recentCommentCount: scored.recentCommentCount ?? 0,
    lastComment: scored.lastComment ?? null,
    hardScore: scored.hardScore,
    llmScore: scored.llmScore ?? null,
    divergent: scored.divergent,
    percentile: scored.percentile ?? null,
    criticalRank: scored.criticalRank ?? null,
    hasLlmAssessment: llm !== null,
    llmTrajectory: llm?.trajectory ?? null,
    llmConfidence: llm?.confidence ?? null,
    llmReasoning: llm?.reasoning ?? null,
    llmRiskFactors: llm?.riskFactors ?? [],
    llmEarlyWarnings: llm?.earlyWarnings ?? [],
    resolutionAccepted: llm?.resolutionAccepted ?? false,

    classificationScore: hard.classificationScore,
    classificationSource: hard.classificationSource,
    classificationLift: hard.classificationLift,
    riskFlagScore: hard.riskFlagScore,
    matchedRiskFlags: hard.matchedRiskFlags ?? [],
    keywordSignalScore: hard.keywordSignalScore,
    matchedKeywordSignals: hard.matchedKeywordSignals ?? [],
    legalHold: hard.legalHold,
    reassignPoints: hard.reassignPoints,
    agingPoints: hard.agingPoints,
    awaitingReviewPoints: hard.awaitingReviewPoints,
    requesterWaitPoints: hard.requesterWaitPoints,
    staleContactPoints: hard.staleContactPoints,
    internalCommentPoints: hard.internalCommentPoints,
    reopenPoints: hard.reopenPoints,
    operationalRiskScore: hard.operationalScore,
    inStayPoints: hard.inStayPoints,
    upcomingCheckInPoints: hard.upcomingCheckInPoints,
    recentStayPoints: hard.recentStayPoints,
    ltvPoints: hard.ltvPoints,
    societePoints: hard.societePoints,
    luxPlusPoints: hard.luxPlusPoints,
    customerRiskScore: hard.customerScore,

    created: ticket.createdAt,
    lastUpdated: ticket.updatedAt,
    statusUpdated: ticket.statusUpdatedAt,
    ticketStatus: toTicketStatusLabel(ticket.status, ticket.customStatusName),
    team: ticket.groupName,
    assignee: ticket.assigneeName,
    teamLeader: ticket.teamLeader ?? null,
    ticketReason: ticket.ticketReason,
    subcategory: ticket.subcategory,
    triageCategory: ticket.triageCategory,
    priority: ticket.priority,
    tags: ticket.tags ?? [],
    replies: ticket.publicComments,
    internalComments: ticket.internalComments,
    agentsTouched: ticket.agentsTouched,
    reassignCount: ticket.reassignCount,
    reopenCount: ticket.reopens,
    requesterWaitMinutes: ticket.requesterWaitMinutes,
    caseAgeDays: Math.floor(ticket.hoursSinceOpen / HOURS_PER_DAY),
    hoursSinceOpen: ticket.hoursSinceOpen,
    hoursAwaitingReview: ticket.hoursAwaitingReview,
    hoursSinceLastContact: ticket.hoursSinceLastContact,
    lastAgentReplyAt: ticket.lastAgentReplyAt,
    sentiment: humanize(ticket.zendeskSentiment),
    sentimentBucket: toSentimentBucket(ticket.zendeskSentiment),

    journeyPhase: toJourneyPhase(
      customer.inStayNow,
      customer.upcomingCheckInDays,
      customer.daysSinceLastCheckOut ?? null,
    ),
    checkInDays: customer.upcomingCheckInDays,
    daysSinceCheckIn: customer.daysSinceCheckIn ?? null,
    currentStayCheckInDate: customer.currentStayCheckInDate ?? null,
    currentStayCheckOutDate: customer.currentStayCheckOutDate ?? null,
    nextCheckInDate: customer.nextCheckInDate ?? null,
    nextCheckOutDate: customer.nextCheckOutDate ?? null,
    lastCheckOutDate: customer.lastCheckOutDate ?? null,
    societeTier: toSocieteTierLabel(customer.societeTier),
    luxPlusMember: customer.luxPlusMember,
    customerLifetimeValue: customer.ltvAud,
    ltvBand: humanize(customer.ltvBand),
    ltvBandKey: toLtvBand(customer.ltvBand),
    daysSinceLastCheckOut: customer.daysSinceLastCheckOut ?? null,
    authorityKeywords: customer.authorityKeywordHits ?? [],
    authorityConfirmed: llm?.authorityConfirmed ?? false,
  };
}

export function toCxTicketRows(
  report: TicketRiskReport | undefined,
  currentUserId: string | null = null,
): Array<CxTicketRow> {
  return (report?.tickets ?? []).map((scored) => toCxTicketRow(scored, currentUserId));
}
